import { __, sprintf } from "@wordpress/i18n";
import {
    useState,
    useEffect,
    useRef,
    useCallback,
    createPortal,
} from "@wordpress/element";
import { Search, ArrowRight, CornerDownLeft } from "lucide-react";
import { SEARCH_INDEX, sectionId } from "../constants/searchIndex";
import { formatShortcut } from "../hooks/useKeyboardShortcuts";

const TAB_COLORS = {
    general: "bg-gray-100 text-gray-600",
    zoom: "bg-blue-50 text-blue-700",
    "zoom-window": "bg-indigo-50 text-indigo-700",
    gallery: "bg-violet-50 text-violet-700",
    lightbox: "bg-purple-50 text-purple-700",
    mobile: "bg-rose-50 text-rose-700",
};

function score(item, words) {
    let s = 0;
    const haystack =
        `${item.label} ${item.description} ${item.section} ${item.tabLabel}`.toLowerCase();
    for (const w of words) {
        if (!haystack.includes(w)) return -1; // all words must match
        if (item.label.toLowerCase().includes(w)) s += 3;
        if (item.section.toLowerCase().includes(w)) s += 1;
    }
    return s;
}

function highlightText(text, words) {
    if (!words.length) return text;
    const regex = new RegExp(
        `(${words
            .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
            .join("|")})`,
        "gi",
    );
    const parts = text.split(regex);
    return parts.map((p, i) =>
        regex.test(p) ? (
            <mark
                key={i}
                className="bg-blue-100 text-blue-800 rounded-[3px] px-px not-italic font-semibold"
            >
                {p}
            </mark>
        ) : (
            p
        ),
    );
}

export default function SearchPalette({
    open,
    onClose,
    onNavigate,
    searchShortcut,
}) {
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const results = query.trim()
        ? SEARCH_INDEX.map((item) => ({ item, s: score(item, words) }))
              .filter(({ s }) => s >= 0)
              .sort((a, b) => b.s - a.s)
              .map(({ item }) => item)
        : SEARCH_INDEX.slice(0, 8);

    // Reset state on open/close
    useEffect(() => {
        if (open) {
            setQuery("");
            setActive(0);
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [open]);

    // Keep active item in view
    useEffect(() => {
        setActive(0);
    }, [query]);

    useEffect(() => {
        const el = listRef.current?.children[active];
        el?.scrollIntoView({ block: "nearest" });
    }, [active]);

    const select = useCallback(
        (item) => {
            onClose();
            onNavigate(item);
        },
        [onClose, onNavigate],
    );

    const onKey = useCallback(
        (e) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
            }
            if (e.key === "Enter" && results[active]) {
                e.preventDefault();
                select(results[active]);
            }
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
        },
        [results, active, select, onClose],
    );

    if (!open) return null;

    const portal = (
        <div
            className="fixed inset-0 z-[99999] flex items-start justify-center pt-[12vh]"
            style={{
                background: "rgba(15,23,42,0.45)",
                backdropFilter: "blur(2px)",
            }}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
                style={{
                    animation:
                        "wcpg-palette-in 0.18s cubic-bezier(0.32,0.72,0,1)",
                }}
            >
                {/* Input row */}
                <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5">
                    <Search className="h-4 w-4 shrink-0 text-gray-400" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onKey}
                        placeholder={__("Search settings…", "wpbean-product-gallery-slider-for-woocommerce")}
                        className="min-w-0 flex-1 bg-transparent text-[14px] text-gray-800 placeholder:text-gray-400 focus:outline-none"
                    />
                    {query && (
                        <button
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setQuery("");
                                inputRef.current?.focus();
                            }}
                            className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-gray-400 hover:bg-gray-100"
                        >
                            {__("Clear", "wpbean-product-gallery-slider-for-woocommerce")}
                        </button>
                    )}
                    <kbd className="shrink-0 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
                        Esc
                    </kbd>
                </div>

                {/* Results */}
                <ul
                    ref={listRef}
                    className="max-h-[380px] overflow-y-auto py-1.5"
                >
                    {results.length === 0 ? (
                        <li className="flex flex-col items-center gap-1.5 py-10 text-center">
                            <Search className="h-7 w-7 text-gray-200" />
                            <p className="text-[13px] text-gray-400">
                                {sprintf(__('No settings found for "%s"', "wpbean-product-gallery-slider-for-woocommerce"), query)}
                            </p>
                        </li>
                    ) : (
                        results.map((item, i) => (
                            <li key={item.key}>
                                <button
                                    type="button"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        select(item);
                                    }}
                                    onMouseEnter={() => setActive(i)}
                                    className={[
                                        "flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors",
                                        i === active
                                            ? "bg-blue-50"
                                            : "hover:bg-gray-50",
                                    ].join(" ")}
                                >
                                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                        <span className="text-[13px] font-medium text-gray-800 leading-snug">
                                            {highlightText(item.label, words)}
                                        </span>
                                        <span className="truncate text-[11.5px] text-gray-400 leading-snug">
                                            {highlightText(item.section, words)}
                                        </span>
                                    </div>
                                    <span
                                        className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                                            TAB_COLORS[item.tab] ||
                                            "bg-gray-100 text-gray-600"
                                        }`}
                                    >
                                        {item.tabLabel}
                                    </span>
                                    {i === active && (
                                        <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-gray-300 rtl:scale-x-[-1]" />
                                    )}
                                </button>
                            </li>
                        ))
                    )}
                </ul>

                {/* Footer hints */}
                <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/70 px-4 py-2">
                    <div className="flex items-center gap-3 text-[11px] text-gray-400">
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border border-gray-200 bg-white px-1 text-[10px]">
                                ↑↓
                            </kbd>{" "}
                            {__("navigate", "wpbean-product-gallery-slider-for-woocommerce")}
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="rounded border border-gray-200 bg-white px-1 text-[10px]">
                                ↵
                            </kbd>{" "}
                            {__("go to setting", "wpbean-product-gallery-slider-for-woocommerce")}
                        </span>
                    </div>
                    {searchShortcut && (
                        <kbd className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-400">
                            {formatShortcut(searchShortcut)}
                        </kbd>
                    )}
                </div>
            </div>
        </div>
    );

    const portalTarget =
        document.getElementById("wpbean-pgs-setting-rule-app") || document.body;
    return createPortal(portal, portalTarget);
}
